import { useEffect, useState } from "react";
import { Copy, Users2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { toast } from "sonner";

export default function Referrals() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const refLink = `${window.location.origin}/signup?ref=${user?.user_code || ""}`;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/referrals");
        if (!mounted) return;
        setItems(data.referrals);
        setCount(data.count);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(refLink);
      toast.success("Referral link copied!");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Header />
      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
        <Link
          to="/dashboard"
          data-testid="back-to-dashboard"
          className="inline-flex items-center gap-1 text-red-700 font-semibold text-sm"
        >
          <ArrowLeft size={16} /> Back to Dashboard
        </Link>

        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-red-700 font-bold text-xl flex items-center gap-2"
              style={{ fontFamily: "Outfit, sans-serif" }}>
            <Users2 size={20} /> My Direct Referrals
          </h2>
          <p className="text-sm text-slate-600 mt-1">Unlimited direct referrals · Earn ₹200 each</p>

          <div className="mt-4 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
            <input
              data-testid="ref-page-link-input"
              readOnly
              value={refLink}
              className="flex-1 bg-transparent outline-none text-xs sm:text-sm text-slate-700"
            />
            <button
              data-testid="ref-page-copy-btn"
              onClick={copyRef}
              className="flex items-center gap-1 bg-red-700 hover:bg-red-800 text-white text-xs font-semibold px-3 py-2 rounded-md active:scale-95 transition-colors"
            >
              <Copy size={14} /> Copy
            </button>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl p-4 bg-gradient-to-br from-purple-500 to-indigo-700 text-white">
              <div className="text-xs uppercase tracking-wider opacity-80">Total Directs</div>
              <div data-testid="total-directs" className="text-2xl font-extrabold mt-1">{count}</div>
            </div>
            <div className="rounded-xl p-4 bg-gradient-to-br from-emerald-500 to-teal-700 text-white">
              <div className="text-xs uppercase tracking-wider opacity-80">Direct Income</div>
              <div data-testid="direct-income-sum" className="text-2xl font-extrabold mt-1">
                ₹{(count * 200).toLocaleString("en-IN")}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 flex justify-center">
              <div className="h-8 w-8 rounded-full border-4 border-red-600 border-t-transparent animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-slate-500" data-testid="no-referrals">
              <Users2 size={36} className="mx-auto text-slate-300 mb-2" />
              <div className="font-semibold text-slate-700">No referrals yet</div>
              <div className="text-sm mt-1">Share your link to start earning ₹200 per join</div>
            </div>
          ) : (
            <ul data-testid="referrals-list" className="divide-y divide-slate-100">
              {items.map((r) => (
                <li key={r.user_code} className="p-4 flex items-start gap-3">
                  <div className="h-10 w-10 shrink-0 rounded-full bg-red-100 text-red-700 flex items-center justify-center font-bold">
                    {r.name?.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-slate-900 truncate">{r.name}</div>
                      <span
                        data-testid={`ref-status-${r.user_code}`}
                        className={`text-xs px-2 py-1 rounded-full font-semibold shrink-0 ${
                          r.activated ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {r.activated ? "Active" : "Pending"}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">{r.user_code} · {r.rank}</div>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <div className="text-slate-600 truncate" data-testid={`ref-phone-${r.user_code}`}>
                        <span className="text-slate-400">Contact:</span>{" "}
                        <span className="font-medium text-slate-800">{r.phone || "—"}</span>
                      </div>
                      <div className="text-slate-600 truncate" data-testid={`ref-email-${r.user_code}`}>
                        <span className="text-slate-400">Email:</span>{" "}
                        <span className="font-medium text-slate-800">{r.email || "—"}</span>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
